import type { OperationVariables, ObservableQuery, WatchQueryFetchPolicy, ApolloClient } from '@apollo/client'
import { TypedDocumentString } from '../utils/apollo'

export interface ApolloWrapInpRes<T extends Record<string, unknown>, Vars extends OperationVariables, Ui> {
  ui: globalThis.Ref<Ui>
  uiCached: globalThis.Ref<Ui>
  loading: globalThis.Ref<boolean>
  clear(i?: Vars): Promise<void>
  query():
    | undefined
    | {
        query: ObservableQuery<T, Vars>
        process: <T>(i: Promise<T>, variables: Vars) => Promise<T>
      }
}

type ClientId = 'post' | 'get'

export interface ApolloWrapInp<T extends Record<string, unknown>, Vars extends OperationVariables, Ui> {
  dev?: string
  clientId?: ClientId
  fetchPolicy?: None | WatchQueryFetchPolicy | (() => WatchQueryFetchPolicy | None)
  nextFetchPolicy?: WatchQueryFetchPolicy | (() => WatchQueryFetchPolicy)
  ui(vals: T | None): Ui
  vars: () => Vars | None
  onLoading?: (i: boolean) => unknown
  onError?: (i: unknown) => unknown
  on?(vals: { ui: Ui; query: T | None; vars: Vars | undefined }): unknown
}

export type ApolloWrap<T extends Record<string, unknown>, Vars extends OperationVariables> = <Ui>(lkm: ApolloWrapInp<T, Vars, Ui>) => ApolloWrapInpRes<T, Vars, Ui>

// oxlint-disable-next-line explicit-module-boundary-types
export function createApolloPluginClient(getClient: (clientId: string) => ApolloClient) {
  function useApolloWrap2<T extends Record<string, unknown>, Vars extends OperationVariables>(queryDocument2: MyDocumentNode): ApolloWrap<T, Vars> {
    return function <Ui>(inp: ApolloWrapInp<T, Vars, Ui>) {
      const logger = (cb: () => unknown[]): void => {
        if (inp.dev) console.log(`[${inp.dev}]`, ...cb())
      }

      let _variablesCache: string | undefined
      let _cacheRes: string | undefined
      let _query: ObservableQuery<T, Vars> | undefined
      let _observer: ReturnType<NN<typeof _query>['subscribe']> | undefined

      const data = shallowReactive({
        _loading: false,
        result: undefined as T | undefined,
        variables: inp.vars(),
        variablesValid: false,
        activated: true,
        ui: inp.ui(undefined) as Ui,
        uiCached: inp.ui(undefined) as Ui,
      })

      function loading(i: boolean) {
        data._loading = i
        inp.onLoading?.(i)
      }

      function error(i: unknown): void {
        const ignore = (): boolean => {
          if (!i) return true
          // oxlint-disable-next-line typescript/no-explicit-any
          const errObj = i as any
          if (typeof errObj === 'object' && (errObj?.name === 'AbortError' || errObj?.message?.includes('The operation was aborted') || errObj?.networkError?.name === 'AbortError')) {
            return true
          }
          const isIgnore = (i: unknown) => helpers.toObject(helpers.toObject(i)?.extensions)?.ignore === true
          const err = i instanceof Error ? i : undefined
          if (isIgnore(err?.cause)) return true
          const arr = helpers.toObject(helpers.toObject(helpers.toObject(err?.cause))?.result)?.errors
          if (Array.isArray(arr) && arr?.some((i) => helpers.toObject(isIgnore(i)))) return true
          return false
        }
        if (ignore()) return
        console.error('[apollo error]', i)
        inp.onError?.(i)
      }

      const stop = () => {
        _observer?.unsubscribe()
        _observer = undefined
        _query = undefined
        _variablesCache = undefined
        _cacheRes = undefined
      }

      const save = (doc: T | undefined, log: string): T | undefined => {
        logger(() => ['save start', log, doc])
        if (data.result === doc) return doc

        const cacheRes = JSON.stringify(doc)
        if (cacheRes === _cacheRes) return doc
        _cacheRes = cacheRes

        const ui = inp.ui(doc)
        data.result = doc
        data.ui = ui
        if (doc) data.uiCached = ui
        loading(false)

        logger(() => ['save new', log, doc])
        inp.on?.({ ui, query: doc, vars: _query?.variables })
        return doc
      }

      async function refetchQuery(varsInp: Vars | None, refetchForce?: boolean): Promise<undefined | T | unknown> {
        const fetchPolicy = getFuncValue(inp.fetchPolicy) || 'cache-first'
        const refetch = refetchForce || helpers.isIn(fetchPolicy, 'cache-and-network', 'network-only', 'no-cache')
        const variables = helpers.isDef(varsInp) && data.activated ? varsInp : undefined
        data.variablesValid = !!variables
        // logger('\n\n\n --- refetchQuery', JSON.stringify(variables, null, 2))

        const variablesStr = JSON.stringify(variables)
        if (!refetch && _variablesCache === variablesStr) {
          // logger('_variablesCache === variablesStr', { variablesStr, refetch })
          return
        }
        _variablesCache = variablesStr

        if (!helpers.isDef(variables)) {
          stop()
          return save(undefined, 'variables empty')
        }

        _query ||= getClient(inp.clientId === 'post' ? 'post' : 'get').watchQuery<T, Vars>({ query: TypedDocumentString.getQueryDocument(queryDocument2), variables, fetchPolicy })
        _observer ||= _query?.subscribe({
          next: (next) => {
            logger(() => ['_observer:next', next])
            if (next.dataState === 'empty' && next.loading && (next.networkStatus === 2 || next.networkStatus === 4)) return
            return save(next.data as T, '_observer:next:res')
          },
          error: (err) => error(err),
        })

        // logger('fetchPolicy', fetchPolicy)
        if (_query && _query?.options.fetchPolicy !== fetchPolicy) _query.options.fetchPolicy = fetchPolicy
        if (_query && refetch) _query.options.variables = undefined as unknown as Vars
        else _query?.setVariables(variables)

        const curr = refetch ? undefined : _query?.getCurrentResult()
        if (curr?.data) {
          return save(curr.data as T, 'curr')
        } else if (curr?.error) {
          error(curr.error)
        } else {
          loading(true)
          try {
            const res = await _query?.refetch(variables)
            if (!res || res?.error) error(res?.error)
            return res?.data as T
          } catch (err) {
            error(err)
          } finally {
            loading(false)
          }
        }
      }

      onScopeDispose(() => stop())
      if (getCurrentInstance()) {
        onDeactivated(() => {
          data.activated = false
          stop()
        })
        onActivated(() => {
          data.activated = true
          refetchQuery(inp.vars())
        })
      }

      watch(
        () => inp.vars(),
        (out) => refetchQuery(out),
        { immediate: true }
      )

      return {
        loading: toRef(() => data._loading),
        ui: toRef(() => data.ui),
        uiCached: toRef(() => data.uiCached),
        async clear(vars) {
          const vars1 = vars || (data.variablesValid ? inp.vars() : undefined)
          if (vars1) await refetchQuery(vars1, true)
        },
        query() {
          if (!_query) return
          return {
            query: _query,
            async process(promise) {
              try {
                loading(true)
                const res = await promise
                const curr = _query?.getCurrentResult().data
                if (curr) save(curr as T, 'load more')
                return res
              } catch (err) {
                error(err)
                throw err
              } finally {
                loading(false)
              }
            },
          }
        },
      }
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  return () => ({ provide: { useApolloWrap2 } })
}
